'use client'

import { useQueryClient } from '@tanstack/react-query'

import { useSharedTRPC } from '../../context'

/**
 * Single owner of cache invalidation for every student roster.
 *
 * Every mutation the table can fire routes through here — changing a class or grade, editing an
 * account, regenerating a PIN, handing out a premium seat, removing a student. Class membership
 * lives on both sides of the relation, so `getAllTeams` is invalidated alongside `listChildren`:
 * the class page counts students from the team rows and would otherwise keep showing the pre-move
 * count. Enrolment flows outside the table reuse this for the same reason.
 */
export function useStudentMutations() {
  const trpc = useSharedTRPC()
  const queryClient = useQueryClient()

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: trpc.myOrganization.listChildren.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.myOrganization.getAllTeams.queryKey() }),
      // The three owned-seat reads a handout moves. `ManageSeatsDialog` invalidates the same set;
      // when this hook refreshed only one of them, a seat given from the row kebab left the roster
      // header's "Total kursi (N tersedia)" and the dashboard's seats alert on their old numbers.
      queryClient.invalidateQueries({ queryKey: trpc.myOrganization.getActiveSeats.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.myOrganization.getSeatPools.queryKey() }),
      /**
       * The SCHEDULED-inclusive read, and the one that kept going stale.
       *
       * It is a separate query key from `getSeatPools`, and it is what the billing plan strip, the
       * roster header total and the seat dialog's pool picker all read now — so a seat freed by
       * removing a student left every one of them counting a child who is gone. A seat assigned to
       * a term that has not started lives ONLY in this read.
       */
      queryClient.invalidateQueries({ queryKey: trpc.myOrganization.getSeatPoolsWithScheduled.queryKey() }),
      // Removing a child changes who is covered from elsewhere too — the billing page reads this to
      // decide whether the plan word is Premium.
      queryClient.invalidateQueries({ queryKey: trpc.myOrganization.getForeignPremiumCoverage.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.myOrganization.getSeatSummary.queryKey() }),
    ])
  }

  return { invalidate }
}
